import React, { useState, useContext, useEffect, useRef } from 'react';
import useAnalytics from '../analytics/useAnalytics';
import { EVT_REGISTER } from '../analytics/events';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Typography,
  Alert,
  MenuItem,
  Grid,
  Chip,
  Autocomplete,
  Snackbar,
  InputAdornment,
  CircularProgress,
  Collapse,
  Button,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CheckCircle, Phone, ExpandMore, ExpandLess } from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { INDIAN_CITIES, COURSE_OPTIONS } from '../utils/indianCities';
import GlassCard from '../components/shared/GlassCard';
import api from '../utils/api';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';
import AuthCard from '../components/auth/AuthCard';
import AuthPrimaryButton from '../components/auth/AuthPrimaryButton';
import { formContainer, formField, checkmarkEntrance } from '../components/auth/animations';

/**
 * RegisterPage
 * Phone/OTP-based student registration. No email or password required.
 * Flow: name + phone → verify OTP → optional fields (city, courses) → submit → auto-login.
 * Mobile: City + Interested Courses collapse behind a disclosure toggle.
 * Uses AuthLayout (DesktopNav + AuthBackground) as the route-level shell.
 */
function RegisterPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { register } = useContext(AuthContext);
  const { track } = useAnalytics();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const authCardRef = useRef(null);
  const reduced = useReducedMotionPreferred();

  // Preserve redirect info from location state
  const from = location.state?.from || '/student';
  const pendingPlanId = location.state?.pendingPlanId;
  const pendingPaymentMethod = location.state?.pendingPaymentMethod;
  const pendingStartDate = location.state?.pendingStartDate;

  const [formData, setFormData] = useState({
    name: '',
    role: 'student',
    age: '',
    city: '',
    phone: '',
    interested_courses: [],
  });

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [otpDeliveryWarning, setOtpDeliveryWarning] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  // Mobile optional fields disclosure toggle
  const [optionalOpen, setOptionalOpen] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Track previous phone to detect changes and reset OTP state
  const [prevPhone, setPrevPhone] = useState('');
  useEffect(() => {
    if (prevPhone && formData.phone !== prevPhone) {
      setOtpSent(false);
      setOtpVerified(false);
      setOtp('');
      setOtpError('');
      setOtpDeliveryWarning(false);
    }
    setPrevPhone(formData.phone);
  }, [formData.phone, prevPhone]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleCoursesChange = (event, newValue) => {
    setFormData({ ...formData, interested_courses: newValue });
  };

  const normalizePhone = (phone) => {
    if (!phone) return '';
    // eslint-disable-next-line no-useless-escape
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');
    if (!cleaned.startsWith('+')) {
      if (cleaned.startsWith('91') && cleaned.length === 12) {
        cleaned = '+' + cleaned;
      } else if (cleaned.length === 10) {
        cleaned = '+91' + cleaned;
      }
    }
    return cleaned;
  };

  const isValidPhone = (phone) => {
    const normalized = normalizePhone(phone);
    return normalized.match(/^\+91\d{10}$/);
  };

  const handleSendOtp = async () => {
    if (!formData.phone) {
      setOtpError('Please enter your phone number');
      return;
    }
    if (!isValidPhone(formData.phone)) {
      setOtpError('Please enter a valid 10-digit Indian phone number');
      return;
    }

    setSendingOtp(true);
    setOtpError('');

    try {
      const res = await api.post('/auth/send-otp', {
        phone: formData.phone,
        purpose: 'registration',
      });
      setOtpSent(true);
      setResendCooldown(60);
      const delivered = res.data?.data?.delivered;
      if (delivered === false) {
        setOtpDeliveryWarning(true);
        setSuccessMessage('');
      } else {
        setOtpDeliveryWarning(false);
        setSuccessMessage('OTP sent to your WhatsApp!');
      }
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'PHONE_EXISTS') {
        setOtpError('This phone number is already registered. Please login instead.');
      } else if (errorCode === 'COOLDOWN') {
        setResendCooldown(err.response?.data?.retryAfter || 60);
        setOtpError(`Please wait ${err.response?.data?.retryAfter || 60} seconds before requesting a new OTP`);
      } else if (errorCode === 'TOO_MANY_REQUESTS') {
        setOtpError('Too many OTP requests. Please try again later.');
      } else {
        setOtpError(err.response?.data?.message || 'Failed to send OTP');
      }
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    setVerifyingOtp(true);
    setOtpError('');

    try {
      await api.post('/auth/verify-otp', {
        phone: formData.phone,
        otp,
        purpose: 'registration',
      });
      setOtpVerified(true);
      setSuccessMessage('Phone number verified successfully!');
    } catch (err) {
      const errorCode = err.response?.data?.code;
      if (errorCode === 'INVALID_OTP') {
        setOtpError(err.response?.data?.message || 'Invalid OTP');
      } else if (errorCode === 'OTP_EXPIRED') {
        setOtpError('OTP has expired. Please request a new one.');
        setOtpSent(false);
      } else if (errorCode === 'MAX_ATTEMPTS') {
        setOtpError('Maximum attempts exceeded. Please request a new OTP.');
        setOtpSent(false);
      } else {
        setOtpError(err.response?.data?.message || 'Failed to verify OTP');
      }
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!otpVerified) {
      setError('Please verify your phone number first');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        name: formData.name,
        role: formData.role,
        phone: formData.phone,
      };
      if (formData.age) submitData.age = parseInt(formData.age, 10);
      if (formData.city) submitData.city = formData.city;
      if (formData.interested_courses.length > 0) {
        submitData.interested_courses = formData.interested_courses.join(', ');
      }

      // register() in AuthContext stores tokens and sets user state (auto-login)
      const result = await register(submitData);
      track(EVT_REGISTER, {});

      if (result.coinsAwarded > 0) {
        // Pulse success before the 2.5s redirect
        authCardRef.current?.pulse();
        setSuccessMessage(`Registration successful! You earned ${result.coinsAwarded} BML Coins for completing your profile!`);
        setTimeout(() => {
          navigate(from, { state: { pendingPlanId, pendingPaymentMethod, pendingStartDate } });
        }, 2500);
      } else {
        authCardRef.current?.pulse();
        navigate(from, { state: { pendingPlanId, pendingPaymentMethod, pendingStartDate } });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Optional fields — rendered inline on desktop, behind disclosure on mobile
  const optionalFields = (
    <>
      {/* City */}
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          select
          label="City"
          name="city"
          value={formData.city}
          onChange={handleChange}
        >
          <MenuItem value="">Select City</MenuItem>
          {INDIAN_CITIES.map((city) => (
            <MenuItem key={city} value={city}>
              {city}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Interested Courses */}
      <Grid item xs={12}>
        <Autocomplete
          multiple
          options={COURSE_OPTIONS}
          value={formData.interested_courses}
          onChange={handleCoursesChange}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip variant="outlined" label={option} {...getTagProps({ index })} />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Interested Courses (Optional)"
              placeholder="Select courses"
              helperText="E.g., UPSC, NEET, IIT-JEE, etc."
            />
          )}
        />
      </Grid>
    </>
  );

  return (
    <>
      <AuthCard
        ref={authCardRef}
        title="A step to book your library"
        subtitle="Join BookMyLibrary to find your perfect study space"
        maxWidth={560}
      >
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Box component="form" onSubmit={handleSubmit}>
          <motion.div variants={formContainer} initial="initial" animate="animate">
            <Grid container spacing={2}>
              {/* Full Name */}
              <Grid item xs={12}>
                <motion.div variants={formField}>
                  <TextField
                    fullWidth
                    label="Full Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </motion.div>
              </Grid>

              {/* Phone Number with OTP Verification */}
              <Grid item xs={12} sm={6}>
                <motion.div variants={formField}>
                  <TextField
                    fullWidth
                    label="WhatsApp Phone Number"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="XXXXXXXXXX"
                    required
                    disabled={otpVerified}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Phone color="action" />
                          <Typography sx={{ ml: 0.5 }}>+91</Typography>
                        </InputAdornment>
                      ),
                      endAdornment: otpVerified && (
                        <InputAdornment position="end">
                          <AnimatePresence>
                            <motion.span
                              key="verified-check"
                              initial={reduced ? false : checkmarkEntrance.initial}
                              animate={checkmarkEntrance.animate}
                              style={{ display: 'flex', alignItems: 'center' }}
                            >
                              <CheckCircle color="success" />
                            </motion.span>
                          </AnimatePresence>
                        </InputAdornment>
                      ),
                    }}
                    helperText={otpVerified ? 'Phone verified' : 'We will send OTP to verify'}
                  />
                </motion.div>
              </Grid>

              {/* OTP Section — shown when phone is entered and not yet verified */}
              {formData.phone && !otpVerified && (
                <Grid item xs={12}>
                  <motion.div
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.24 }}
                  >
                    <GlassCard
                      blur={8}
                      sx={{
                        p: 2,
                        borderRadius: 3,
                        border: (t) => `1px solid ${t.palette.primary.soft ?? 'rgba(242,101,34,0.18)'}`,
                      }}
                      aria-live="polite"
                    >
                      {otpError && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                          {otpError}
                        </Alert>
                      )}

                      {otpDeliveryWarning && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          We couldn't deliver the code via WhatsApp just now. If you don't receive it, tap Resend in a few seconds.
                        </Alert>
                      )}

                      {!otpSent ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Typography variant="body2" color="text.secondary" flex={1}>
                            Click to verify your WhatsApp number
                          </Typography>
                          <Button
                            variant="contained"
                            onClick={handleSendOtp}
                            disabled={sendingOtp || !isValidPhone(formData.phone)}
                            sx={{
                              background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderRadius: 999,
                              '&:hover': {
                                background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                              },
                            }}
                          >
                            {sendingOtp ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
                          </Button>
                        </Box>
                      ) : (
                        <Box>
                          <Typography variant="body2" color="text.secondary" mb={2}>
                            Enter the 6-digit OTP sent to your WhatsApp
                          </Typography>
                          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                            <TextField
                              size="small"
                              label="Enter OTP"
                              value={otp}
                              onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              inputProps={{
                                maxLength: 6,
                                style: { letterSpacing: '0.5em', textAlign: 'center' },
                              }}
                              sx={{ width: 150 }}
                            />
                            <Button
                              variant="contained"
                              onClick={handleVerifyOtp}
                              disabled={verifyingOtp || otp.length !== 6}
                              sx={{
                                background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                                textTransform: 'none',
                                fontWeight: 600,
                                borderRadius: 999,
                              }}
                            >
                              {verifyingOtp ? <CircularProgress size={24} color="inherit" /> : 'Verify'}
                            </Button>
                            <Button
                              variant="text"
                              onClick={handleSendOtp}
                              disabled={sendingOtp || resendCooldown > 0}
                              size="small"
                              sx={{ textTransform: 'none' }}
                            >
                              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend OTP'}
                            </Button>
                          </Box>
                        </Box>
                      )}
                    </GlassCard>
                  </motion.div>
                </Grid>
              )}

              {/* Optional fields: inline on desktop, behind disclosure on mobile */}
              {isMobile ? (
                <Grid item xs={12}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setOptionalOpen((o) => !o)}
                    endIcon={optionalOpen ? <ExpandLess /> : <ExpandMore />}
                    sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
                    aria-expanded={optionalOpen}
                    aria-controls="optional-fields-collapse"
                  >
                    Add a few more details (optional)
                  </Button>
                  <Collapse in={optionalOpen} id="optional-fields-collapse">
                    <Box sx={{ mt: 1 }}>
                      <Grid container spacing={2}>
                        {optionalFields}
                      </Grid>
                    </Box>
                  </Collapse>
                </Grid>
              ) : (
                optionalFields
              )}

              {/* Submit */}
              <Grid item xs={12}>
                <motion.div variants={formField}>
                  <Box sx={{ mt: 1 }}>
                    <AuthPrimaryButton
                      type="submit"
                      loading={loading}
                      disabled={!otpVerified}
                    >
                      {loading ? 'Registering…' : (pendingPlanId ? 'Confirm your Booking' : 'Create Account')}
                    </AuthPrimaryButton>
                    {formData.phone && !otpVerified && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                        textAlign="center"
                        mt={1}
                      >
                        Please verify your phone number to continue
                      </Typography>
                    )}
                  </Box>
                </motion.div>
              </Grid>
            </Grid>
          </motion.div>

          <Box mt={3} textAlign="center">
            <Typography variant="body2">
              Already have an account?{' '}
              <Link
                to="/login"
                state={{ from, pendingPlanId, pendingPaymentMethod, pendingStartDate }}
                style={{ textDecoration: 'none', color: '#F26522', fontWeight: 600 }}
              >
                Login here
              </Link>
            </Typography>
          </Box>
        </Box>
      </AuthCard>

      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSuccessMessage('')}
          severity="success"
          variant="filled"
          sx={{ width: '100%', fontWeight: 600, fontSize: '1rem' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default RegisterPage;
